/**
 * Salvage component generation (M2-10, spec §6.1-§6.2): the parts of a combat, death, contract or derelict wreck,
 * each with its condition, mass and cut anchors, all seeded so a saved wreck regenerates exactly from its seed and
 * layout (the save keeps only the state that changed, plan §2.7 `WreckRecord`).
 *
 * Not per-tick code: generation runs when a wreck spawns or is restored, so it may allocate. `writePartSpec` copies
 * a spec into the M2-17 part pool columns; `contentsMassT` and `contentsBaseValue` are allocation-free (the scanner
 * uses them).
 */
import { GOOD_BASE_PRICE, GOOD_IDS, GOOD_MASS_T } from '../career/parts'
import { MAKER_IDS, type CargoContents, type MakerId } from '../career/types'
import { createRng, nextFloat, range, type Rng } from '../core/rng'
import type { Vec3 } from '../core/math'
import type { AssetsFile } from '../data/assetSchema'
import {
  COMPONENT_TYPES,
  MAX_ANCHORS_PER_PART,
  MAX_PARTS_PER_WRECK,
  MK_COMPONENT_TYPES,
  WARE_IDS,
  componentTypeIndex,
  type ComponentType,
  type LayoutSlot,
  type M2Data,
  type SalvageFileRaw,
} from '../data/m2Schemas'
import type { RegionId } from '../galaxy/types'
import type { PartPool } from '../space/contracts'
import { hash3, seededUnit } from '../space/hash'
import { boxCentre, slotPosition, type SlotModel } from './layout'

/** The M2 data generation reads. */
export type SalvageGenData = Pick<M2Data, 'salvage' | 'wares'>

/** Which rule set builds a ship wreck's parts. Derelict sections use `generateDerelictParts`. */
export type ShipWreckKind = 'combat' | 'contract' | 'death'

/** A cut anchor in the wreck frame. */
export interface AnchorSpec {
  localPos: Vec3
  /** Seconds to cut at beam multiplier 1 (`salvage.json beam.cutSeconds`). */
  cutSeconds: number
  /** Cutting it vents coolant (the reactor's last anchor, spec §6.3). */
  coolant: boolean
}

/** One salvageable part as generated. */
export interface PartSpec {
  /** Part index within its wreck (the secured key is `${wreckKey}:${index}`). */
  index: number
  type: ComponentType
  /** Position in `COMPONENT_TYPES`. */
  typeIndex: number
  /** Wreck-frame centre of the part (m). */
  localPos: Vec3
  /** Unit wreck-frame direction the anchors face (away from the hull centre). */
  outward: Vec3
  /** Part radius (m): the prop model's bounding radius. */
  radius: number
  /** 0..1. */
  condition: number
  /** Tonnes of the part itself (what the cargo racks must take, `maxPartMassT`). */
  massT: number
  /** Tonnes it takes in the hold: `massT` plus a pod's contents. */
  holdT: number
  anchors: AnchorSpec[]
  /** A cargo pod's contents (the player's death pod), else null. */
  contents: CargoContents | null
  /** Mk 1-3 of a Mk-carrying type (`MK_COMPONENT_TYPES`), rolled by region on its own stream; 0 for the rest. */
  mk: number
  /**
   * Who made the part (M8 MC-f5a): derelict parts are the First Charter's, ship wreck parts take the caller's maker;
   * null = unknown (the player's death pod, and wrecks whose source names none). Never drawn, so no stream moves.
   */
  maker: MakerId | null
  /** The Rush-pattern provenance tag (M8 MC-h1 sets it on derelict parts; MC-f5a plumbs it). null = not Rush. */
  provenance: 'rush' | null
}

/** Pool code of "no maker" (`PartPool.maker`); a known maker is its `MAKER_IDS` index. */
export const NO_MAKER = -1

/** `PartPool.maker` code of `maker` (allocation-free). */
export function makerCode(maker: MakerId | null): number {
  return maker === null ? NO_MAKER : MAKER_IDS.indexOf(maker)
}

/** The maker of pool code `code`, or null for `NO_MAKER` or an unknown code (allocation-free). */
export function makerOfCode(code: number): MakerId | null {
  return code >= 0 && code < MAKER_IDS.length ? MAKER_IDS[code] : null
}

// Anchor placement on the outward hemisphere: polar angle from the outward axis (radians). Geometry, not tuning:
// kept away from the pole so anchors spread around the part, and short of the equator so they face the player.
const ANCHOR_POLAR_MIN = 0.45
const ANCHOR_POLAR_MAX = 1.2
/** Azimuth jitter as a share of the even spacing between anchors. */
const ANCHOR_AZIMUTH_JITTER = 0.35
/** Radius used when a component's prop model is missing from `assets.json` (m). */
const FALLBACK_PART_RADIUS = 1
/** A part closer than this to the reference centre faces +Y. */
const MIN_OUTWARD = 1e-6

// Hash salts: one per independent stream, so adding a draw to one never shifts another.
const SALT_PART = 0x5a17a6e1
const SALT_DERELICT = 0x5a17de7e
/** The Mk roll (economy plan SY-4a): its own stream, so no condition, mass or anchor draw moves. */
const SALT_MK = 0x5a17b4c3
/** The Rush-pattern roll (M8 MC-h1): its own stream, so no condition, mass, anchor or Mk draw moves. */
const SALT_RUSH = 0x5a1752a5

/** Who made every derelict part (M8 MC-f5a): the First Charter, whose ships the Rush left behind. */
const DERELICT_MAKER: MakerId = 'first_charter'

/**
 * What a derelict site is to the Rush-pattern roll (M8 MC-h1): `plain` rolls at its region's share, `graveyard` (a
 * derelict-graveyard system) at the share × `salvage.json rush.graveyardMult`, and `sealed` (the Harrow, Okafor's
 * lab and every other story site) never.
 */
export const DERELICT_SITES = ['plain', 'graveyard', 'sealed'] as const
export type DerelictSite = (typeof DERELICT_SITES)[number]

/**
 * The chance a derelict part of `region` at a `site` is Rush-pattern (M8 MC-h1, decision 13): the region's
 * `salvage.json rush.byRegion` share (gate 0), × `rush.graveyardMult` in a graveyard, 0 when sealed, at most 1. No
 * budget and no decay: the derelicts themselves are the limit.
 */
export function rushChance(region: RegionId, site: DerelictSite, salvage: Pick<SalvageFileRaw, 'rush'>): number {
  if (site === 'sealed') return 0
  const share = salvage.rush.byRegion[region]
  return Math.min(1, site === 'graveyard' ? share * salvage.rush.graveyardMult : share)
}

/**
 * Whether part stream `stream` of `seed` is Rush-pattern at `chance` (`rushChance`; M8 MC-h1). Drawn on its own
 * salt, so it moves no other draw; deterministic, so a saved derelict re-derives the same tag. Allocation-free.
 */
export function rollRush(seed: number, stream: number, chance: number): boolean {
  return chance > 0 && seededUnit(seed, stream, SALT_RUSH) < chance
}

/**
 * The region a part's Mk is rolled for when the caller names none. The salvage runtime starts on it and the bridge
 * sets the entered system's region on every system entry (economy plan SY-4b, `SalvageRuntime.setRegion`): derelict
 * and ship wreck parts roll for that region (a wreck's source ship has no known Mk; D-TBD Phase 1 follow-ups).
 */
export const DEFAULT_SALVAGE_REGION: RegionId = 'gate'

/** 1 = the component type at that `COMPONENT_TYPES` index carries a Mk. */
const MK_TYPE: Uint8Array = Uint8Array.from(COMPONENT_TYPES, (t) => ((MK_COMPONENT_TYPES as readonly string[]).includes(t) ? 1 : 0))

/** Whether component type index `typeIndex` carries a Mk (allocation-free). */
export function carriesMk(typeIndex: number): boolean {
  return MK_TYPE[typeIndex] === 1
}

/**
 * The Mk of part stream `stream` of `seed` (economy plan SY-4a): 0 for a type without one, else 1-3 drawn from the
 * region's `salvage.json tiers.byRegion` shares on the Mk salt. Allocation-free and deterministic, so a saved part
 * without a stored Mk re-derives the same one.
 */
export function rollMk(seed: number, stream: number, typeIndex: number, region: RegionId, salvage: Pick<SalvageFileRaw, 'tiers'>): number {
  if (!carriesMk(typeIndex)) return 0
  const shares = salvage.tiers.byRegion[region]
  const u = seededUnit(seed, stream, SALT_MK)
  if (u < shares[0]) return 1
  return u < shares[0] + shares[1] ? 2 : 3
}

/** Bounding radius of each component type's prop model (indexed by `COMPONENT_TYPES`). */
export function componentRadii(data: Pick<M2Data, 'wares'>, assets: Pick<AssetsFile, 'models'>): number[] {
  return data.wares.components.map((c) => assets.models.find((m) => m.id === c.propAsset)?.bounds.radius ?? FALLBACK_PART_RADIUS)
}

/** Credits of a component at `condition`: `baseValue × (0.3 + 0.7 × condition)` (spec §6.2; a pod is worth its contents). */
export function partValue(type: ComponentType, condition: number, data: Pick<M2Data, 'wares'>): number {
  return partValueByIndex(componentTypeIndex(type), condition, data)
}

/** `partValue` by component type index (allocation-free; condition clamped to 0..1). */
export function partValueByIndex(typeIndex: number, condition: number, data: Pick<M2Data, 'wares'>): number {
  const c = condition < 0 ? 0 : condition > 1 ? 1 : condition
  return data.wares.components[typeIndex].baseValue * (0.3 + 0.7 * c)
}

/** Condition band codes (economy plan SY-1): the `WreckScan.band` column and the scan row label index. */
export const CONDITION_BAND = { intact: 0, serviceable: 1, wrecked: 2 } as const
export type ConditionBand = (typeof CONDITION_BAND)[keyof typeof CONDITION_BAND]

/**
 * The condition band of a part (`salvage.json bands`): Intact at `bands.intact` or above, Serviceable from
 * `bands.serviceable` (inclusive) up to below `bands.intact`, Wrecked below `bands.serviceable`. Derived, never
 * stored. Allocation-free.
 */
export function conditionBand(condition: number, bands: Readonly<SalvageFileRaw['bands']>): ConditionBand {
  if (condition >= bands.intact) return CONDITION_BAND.intact
  if (condition >= bands.serviceable) return CONDITION_BAND.serviceable
  return CONDITION_BAND.wrecked
}

/** Tonnes held in a pod's contents (wares, goods (SY-2b) and components). Allocation-free. */
export function contentsMassT(contents: Readonly<CargoContents>): number {
  let t = 0
  for (let i = 0; i < WARE_IDS.length; i++) t += contents.wares[WARE_IDS[i]]
  const goods = contents.goods
  if (goods !== undefined) {
    for (let i = 0; i < GOOD_IDS.length; i++) t += (goods[GOOD_IDS[i]] ?? 0) * GOOD_MASS_T[GOOD_IDS[i]]
  }
  for (let i = 0; i < contents.components.length; i++) t += contents.components[i].massT
  return t
}

/**
 * Base-price value of a pod's contents (wares at base price, goods at `GOOD_BASE_PRICE`: the strategic base price
 * (SY-2b), Rush spares at `career.json rushSpares.base` (MC-h3); components at their recorded value). Allocation-free.
 */
export function contentsBaseValue(contents: Readonly<CargoContents>, data: Pick<M2Data, 'wares'>): number {
  let v = 0
  for (let i = 0; i < WARE_IDS.length; i++) v += contents.wares[WARE_IDS[i]] * data.wares.wares[i].basePrice
  const goods = contents.goods
  if (goods !== undefined) {
    for (let i = 0; i < GOOD_IDS.length; i++) {
      const g = GOOD_IDS[i]
      v += (goods[g] ?? 0) * GOOD_BASE_PRICE[g]
    }
  }
  for (let i = 0; i < contents.components.length; i++) v += contents.components[i].value
  return v
}

/**
 * Parts of a ship wreck from its `salvage.json` layout and ship model (spec §6.2).
 * - `combat`: each slot yields a part with `wrecks.partChance`; at least one part always survives (the slot that
 *   rolled closest to surviving), so every combat wreck is worth a look.
 * - `contract`: as combat, but every slot of `requiredType` is present; a layout without that type gets its first
 *   slot retyped, so a recovery target always exists.
 * - `death`: only the cargo pod slot (see `generateDeathPod`).
 * Conditions are seeded in `wrecks.condition` (the death pod is 1), masses in the type's `massT` range, and each part
 * gets its type's anchor count on the outward hemisphere at its radius; a reactor's last anchor is the coolant one.
 * Mk-carrying parts roll their Mk for `region` (`rollMk`, stream = the slot). Every part carries `maker` (M8 MC-f5a;
 * null = unknown); it takes no draw.
 */
export function generateWreckParts(
  seed: number,
  layout: readonly LayoutSlot[],
  model: SlotModel,
  data: SalvageGenData,
  kind: ShipWreckKind,
  radii: readonly number[],
  requiredType?: ComponentType,
  region: RegionId = DEFAULT_SALVAGE_REGION,
  maker: MakerId | null = null,
): PartSpec[] {
  const centre = boxCentre(model, { x: 0, y: 0, z: 0 })
  const slots = kind === 'death' ? layout.filter((s) => s.type === 'cargoPod') : contractLayout(layout, kind === 'contract' ? requiredType : undefined)
  const chance = data.salvage.wrecks.partChance
  const keep: boolean[] = []
  let bestRoll = Infinity
  let best = -1
  for (let k = 0; k < slots.length; k++) {
    const roll = nextFloat(createRng(hash3(seed, k, SALT_PART ^ 0x1)))
    const forced = kind === 'death' || (requiredType !== undefined && kind === 'contract' && slots[k].type === requiredType)
    keep.push(forced || roll < chance)
    if (roll < bestRoll) {
      bestRoll = roll
      best = k
    }
  }
  if (best >= 0 && !keep.some(Boolean)) keep[best] = true

  const parts: PartSpec[] = []
  for (let k = 0; k < slots.length && parts.length < MAX_PARTS_PER_WRECK; k++) {
    if (!keep[k]) continue
    const localPos = { x: 0, y: 0, z: 0 }
    slotPosition(slots[k].at, model, localPos)
    const rng = createRng(hash3(seed, k, SALT_PART))
    const condition = kind === 'death' ? 1 : undefined
    const mk = rollMk(seed, k, componentTypeIndex(slots[k].type), region, data.salvage)
    parts.push(buildPart(parts.length, slots[k].type, localPos, centre, rng, data, radii, condition, mk, maker))
  }
  return parts
}

/**
 * The player's death wreck: one cargo pod at the layout's cargo pod slot (the box centre when the layout has none),
 * condition 1, holding `contents`. Its `holdT` is the pod plus the contents; `massT` (the rack check) is the pod alone.
 */
export function generateDeathPod(
  seed: number,
  layout: readonly LayoutSlot[],
  model: SlotModel,
  data: SalvageGenData,
  radii: readonly number[],
  contents: CargoContents,
): PartSpec {
  let pod = generateWreckParts(seed, layout, model, data, 'death', radii)[0] as PartSpec | undefined
  if (!pod) {
    const centre = boxCentre(model, { x: 0, y: 0, z: 0 })
    pod = buildPart(0, 'cargoPod', { ...centre }, centre, createRng(hash3(seed, 0, SALT_PART)), data, radii, 1, 0, null)
  }
  pod.contents = contents
  pod.holdT = pod.massT + contentsMassT(contents)
  return pod
}

/**
 * M8 TR-8b1: the one cargo pod of a jettison wreck (`SalvageRuntime.spawnJettisonPod`), at the wreck origin, holding
 * exactly `contents` (`holdT` = its own mass + `contentsMassT`). Condition 1, no Mk, no maker; mass and anchors
 * are seeded from `seed` like a death pod's (the pod is freed at once, so its anchors are never cut).
 */
export function generateJettisonPod(seed: number, data: SalvageGenData, radii: readonly number[], contents: CargoContents): PartSpec {
  const origin = { x: 0, y: 0, z: 0 }
  const pod = buildPart(0, 'cargoPod', { x: 0, y: 0, z: 0 }, origin, createRng(hash3(seed, 0, SALT_PART)), data, radii, 1, 0, null)
  pod.contents = contents
  pod.holdT = pod.massT + contentsMassT(contents)
  return pod
}

/**
 * Parts of the First Rush derelict, one list per `salvage.json derelict.sections` entry, in the section frame
 * (origin at the section centre, the derelict's rotation). A part's fixed `condition` wins over the seeded one;
 * anchors face away from the section centre. Mk-carrying parts roll their Mk for `region`. Every derelict part is
 * the First Charter's (`maker`, M8 MC-f5a; no draw), and each rolls its Rush-pattern tag for `region` and `site`
 * (`rushChance`, M8 MC-h1) on its own salt.
 */
export function generateDerelictParts(
  seed: number,
  data: SalvageGenData,
  radii: readonly number[],
  region: RegionId = DEFAULT_SALVAGE_REGION,
  site: DerelictSite = 'plain',
): PartSpec[][] {
  const origin = { x: 0, y: 0, z: 0 }
  const rush = rushChance(region, site, data.salvage)
  return data.salvage.derelict.sections.map((section, s) =>
    section.parts.map((p, k) => {
      const stream = s * MAX_PARTS_PER_WRECK + k
      const rng = createRng(hash3(seed, stream, SALT_DERELICT))
      const localPos = { x: p.offset[0], y: p.offset[1], z: p.offset[2] }
      const mk = rollMk(seed, stream, componentTypeIndex(p.type), region, data.salvage)
      const part = buildPart(k, p.type, localPos, origin, rng, data, radii, p.condition, mk, DERELICT_MAKER)
      if (rollRush(seed, stream, rush)) part.provenance = 'rush'
      return part
    }),
  )
}

/**
 * Copies a spec into part-pool slot `slot` (wreck-frame columns, condition, Mk, maker, Rush flag, mass, anchors, contents; cut progress,
 * freed, secured and tractored reset). It does not touch `alive`, `high`, `live` or the world columns: the runtime
 * (M2-17) owns allocation and the per-tick world transform.
 */
export function writePartSpec(pool: PartPool, slot: number, wreck: number, spec: Readonly<PartSpec>): void {
  pool.wreck[slot] = wreck
  pool.index[slot] = spec.index
  pool.type[slot] = spec.typeIndex
  pool.lx[slot] = spec.localPos.x
  pool.ly[slot] = spec.localPos.y
  pool.lz[slot] = spec.localPos.z
  pool.condition[slot] = spec.condition
  pool.mk[slot] = spec.mk
  pool.maker[slot] = makerCode(spec.maker)
  pool.rush[slot] = spec.provenance === 'rush' ? 1 : 0
  pool.massT[slot] = spec.massT
  pool.anchorCount[slot] = spec.anchors.length
  const base = slot * MAX_ANCHORS_PER_PART
  for (let a = 0; a < MAX_ANCHORS_PER_PART; a++) {
    const anchor = spec.anchors[a]
    pool.alx[base + a] = anchor ? anchor.localPos.x : 0
    pool.aly[base + a] = anchor ? anchor.localPos.y : 0
    pool.alz[base + a] = anchor ? anchor.localPos.z : 0
    pool.cut[base + a] = 0
    pool.coolant[base + a] = anchor?.coolant ? 1 : 0
    pool.cutSeconds[base + a] = anchor ? anchor.cutSeconds : 0
  }
  pool.freed[slot] = 0
  pool.secured[slot] = 0
  pool.tractored[slot] = 0
  pool.contents[slot] = spec.contents
}

/** The layout a contract wreck uses: the layout itself, or with its first slot retyped when it lacks `requiredType`. */
function contractLayout(layout: readonly LayoutSlot[], requiredType: ComponentType | undefined): readonly LayoutSlot[] {
  if (requiredType === undefined || layout.length === 0 || layout.some((s) => s.type === requiredType)) return layout
  return [{ ...layout[0], type: requiredType }, ...layout.slice(1)]
}

function buildPart(
  index: number,
  type: ComponentType,
  localPos: Vec3,
  centre: Readonly<Vec3>,
  rng: Rng,
  data: SalvageGenData,
  radii: readonly number[],
  fixedCondition: number | undefined,
  mk: number,
  maker: MakerId | null,
): PartSpec {
  const typeIndex = componentTypeIndex(type)
  const def = data.wares.components[typeIndex]
  const cfg = data.salvage
  const seededCondition = range(rng, cfg.wrecks.condition[0], cfg.wrecks.condition[1])
  const condition = fixedCondition ?? seededCondition
  const massT = range(rng, def.massT[0], def.massT[1])
  const [lo, hi] = def.anchors
  const count = Math.min(MAX_ANCHORS_PER_PART, lo + Math.floor(nextFloat(rng) * (hi - lo + 1)))
  const radius = radii[typeIndex] ?? FALLBACK_PART_RADIUS

  const outward = unitOr(localPos.x - centre.x, localPos.y - centre.y, localPos.z - centre.z)
  // An orthonormal basis (t1, t2) around the outward axis.
  const helper = Math.abs(outward.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 }
  const t1 = unitOr(helper.y * outward.z - helper.z * outward.y, helper.z * outward.x - helper.x * outward.z, helper.x * outward.y - helper.y * outward.x)
  const t2 = {
    x: outward.y * t1.z - outward.z * t1.y,
    y: outward.z * t1.x - outward.x * t1.z,
    z: outward.x * t1.y - outward.y * t1.x,
  }

  const phase = nextFloat(rng) * Math.PI * 2
  const anchors: AnchorSpec[] = []
  for (let a = 0; a < count; a++) {
    const azimuth = phase + ((a + (nextFloat(rng) - 0.5) * ANCHOR_AZIMUTH_JITTER) / count) * Math.PI * 2
    const polar = range(rng, ANCHOR_POLAR_MIN, ANCHOR_POLAR_MAX)
    const sp = Math.sin(polar)
    const cp = Math.cos(polar)
    const ca = Math.cos(azimuth) * sp
    const sa = Math.sin(azimuth) * sp
    anchors.push({
      localPos: {
        x: localPos.x + radius * (outward.x * cp + t1.x * ca + t2.x * sa),
        y: localPos.y + radius * (outward.y * cp + t1.y * ca + t2.y * sa),
        z: localPos.z + radius * (outward.z * cp + t1.z * ca + t2.z * sa),
      },
      cutSeconds: range(rng, cfg.beam.cutSeconds[0], cfg.beam.cutSeconds[1]),
      coolant: def.hazard === 'coolant' && a === count - 1,
    })
  }
  return {
    index,
    type,
    typeIndex,
    localPos,
    outward,
    radius,
    condition,
    massT,
    holdT: massT,
    anchors,
    contents: null,
    mk,
    maker,
    provenance: null,
  }
}

function unitOr(x: number, y: number, z: number): Vec3 {
  const len = Math.hypot(x, y, z)
  return len < MIN_OUTWARD ? { x: 0, y: 1, z: 0 } : { x: x / len, y: y / len, z: z / len }
}

